/* tslint:disable:no-duplicate-string */
import { jwtDecode } from 'jwt-decode';
import { isEmpty } from 'lodash';
import { Inject, Injectable } from '../../injection';
import { SerializableParams } from '../../utils';

import { MobizSession } from '../data/mobiz-session.do';
import { TokenClaims } from '../data/token-claims.do';
import { AccessDeniedError } from '../errors/access-denied.error';

import { AuthGrantType } from './auth-token-grant';
import { IAuthTokenService } from './auth-token.interface';
import { CONFIGURATION_SERVICE, IConfigurationService } from './configuration.interface';
import { IMobizStorageService } from './mobiz-storage.interface';

@Injectable()
export class OAuth2TokenService implements IAuthTokenService {
	private readonly MOBIZ_SESSION_KEY: string = 'MobizSession';
	private currentSession?: MobizSession;

	constructor(
		@Inject(IMobizStorageService) private readonly storage: IMobizStorageService,
		@Inject(CONFIGURATION_SERVICE) private readonly configurationService: IConfigurationService
	) {
		this.legacySessionCleanup();
		storage.registerKeyForClear(this.MOBIZ_SESSION_KEY);
	}

	public get claims(): TokenClaims {
		return this.getClaimsForAccessToken();
	}

	public set claims(_value: TokenClaims) {
		throw new Error('Disallowed, please update the IAuthTokenService.session value.');
	}

	public get session(): MobizSession {
		if (this.currentSession) {
			return this.currentSession;
		}
		const sessionData: unknown = this.storage.get(this.MOBIZ_SESSION_KEY);
		if (!sessionData) {
			// TODO: Only return valid sessions!
			return new MobizSession();
		}
		this.currentSession = new MobizSession(sessionData);
		return this.currentSession;
	}

	public set session(value: MobizSession) {
		this.currentSession = value;
		this.storage.set(this.MOBIZ_SESSION_KEY, value);
	}

	public get accessToken(): string | null {
		return this.session?.accessToken || null;
	}

	public set accessToken(_value: string | null) {
		throw new Error('Disallowed, please update the IAuthTokenService.session value.');
	}

	public get refreshToken(): string | null {
		return this.session?.refreshToken || null;
	}

	public set refreshToken(_value: string | null) {
		throw new Error('Disallowed, please update the IAuthTokenService.session value.');
	}

	public clearAuthData(): void {
		this.storage.remove(this.MOBIZ_SESSION_KEY);
	}

	public getIdentificationHeaders(authGrantType: AuthGrantType): Headers {
		const currentSession: MobizSession | undefined = this.session;
		let headers: Record<string, string>;
		// Use AuthGrantType from current session when adding identification headers.
		const currentGrantType =
			authGrantType === AuthGrantType.RefreshToken && currentSession ? currentSession.grantType : authGrantType;
		switch (currentGrantType) {
			case AuthGrantType.ClientCredentials:
				if (!this.configurationService.clientCredentialsClientId) {
					throw new Error('The clientCredentialsClientId configuration is missing.');
				}
				if (!this.configurationService.clientCredentialsClientVersion) {
					throw new Error('The clientCredentialsClientVersion configuration is missing.');
				}
				headers = {
					'X-ClientId': this.configurationService.clientCredentialsClientId,
					'X-ClientVersion': this.configurationService.clientCredentialsClientVersion,
				};
				break;
			case AuthGrantType.Password:
				headers = {
					'X-ClientId': this.configurationService.clientId,
					'X-ClientVersion': this.configurationService.clientVersion,
				};
				break;
			default:
				throw new Error(`Unsupported AuthGrantType provided: ${currentGrantType}`);
		}
		if (currentSession.tokenType && currentSession.accessToken) {
			headers['Authorization'] = `${currentSession.tokenType} ${currentSession.accessToken}`;
		}
		return new Headers(headers);
	}

	public getTokenRefreshRequestBody(grantType: AuthGrantType): SerializableParams {
		const session: MobizSession | null | undefined = this.session;
		if (!session) {
			throw new AccessDeniedError('Please log in.');
		}
		switch (grantType) {
			case AuthGrantType.ClientCredentials:
				return this.getClientCredentialsRequestPayload(session);
			case AuthGrantType.Password:
				return this.getPasswordRequestPayload(session);
			default:
				throw new Error('Unsupported grant type.');
		}
	}

	private getClientCredentialsRequestPayload(session: MobizSession): SerializableParams {
		if (!session.refreshToken) {
			throw new Error('Invalid session.');
		}
		if (!this.configurationService.clientCredentialsClientId) {
			throw new Error('The clientCredentialsClientId configuration is missing.');
		}
		if (session.grantType !== AuthGrantType.ClientCredentials) {
			throw new AccessDeniedError(`Invalid session type. Can not refresh ${session.grantType} grantType.`);
		}
		return new SerializableParams({
			client_id: this.configurationService.clientCredentialsClientId,
			grant_type: AuthGrantType.RefreshToken,
			refresh_token: session.refreshToken,
		});
	}

	private getPasswordRequestPayload(session: MobizSession): SerializableParams {
		if (!session.refreshToken) {
			throw new Error('Invalid session.');
		}
		if (session.grantType !== AuthGrantType.Password) {
			throw new AccessDeniedError(`Invalid session type. Can not refresh ${session.grantType} grantType.`);
		}
		return new SerializableParams({
			client_id: this.configurationService.clientId,
			grant_type: AuthGrantType.RefreshToken,
			refresh_token: session.refreshToken,
		});
	}

	/**
	 * Decodes the access token and extracts the claims.
	 * If no access token is passed it will fallback to the access token stored in the storage.
	 * @param access_token The access token.
	 */
	private getClaimsForAccessToken(access_token?: string | null): TokenClaims | never {
		if (access_token && access_token.length) {
			return new TokenClaims(jwtDecode(access_token));
		}
		const session = this.session;
		if (!session) {
			throw new Error('No session stored');
		}
		return session.getClaimsForAccessToken();
	}

	/**
	 * TODO: Remove this cleanup after a while.
	 */
	private legacySessionCleanup(): void {
		const accessToken = this.storage.get<string>('access_token');
		const refreshToken = this.storage.get<string>('refresh_token');
		this.storage.remove('access_token');
		this.storage.remove('refresh_token');
		if (isEmpty(accessToken) || isEmpty(refreshToken)) {
			return;
		}
		const session = new MobizSession();
		session.accessToken = accessToken;
		session.refreshToken = refreshToken;
		session.tokenType = 'Bearer';
		session.expiresIn = 0;
		session.grantType = AuthGrantType.Password;
		this.session = session;
	}
}
